/* eslint-disable no-use-before-define */
import React, { useEffect, useState } from 'react';
import _ from 'underscore';
import { titleize } from 'underscore.string';
import CargomaticModal from 'components/v2/modal';
import CargomaticInput from 'components/v2/input';
import ErrorLabel from 'components/v2/forms/ErrorLabel';
import CargomaticSelect from 'components/v2/select';
import moment from 'moment';
import { Translation, useTranslation } from 'react-i18next';
export default function ShipperCostAdjustment({ selectedLineItem, shipment, show: showModal, equipments, onSave, onHide, }) {
    const { t } = useTranslation();
    const [adjustmentData, setAdjustmentData] = useState(() => getInitialValue());
    const [errors, setErrors] = useState({
        note: null,
        reasonCode: null,
        shipperCost: null,
    });
    useEffect(() => {
        setAdjustmentData(() => getInitialValue());
    }, [showModal, selectedLineItem]);
    useEffect(() => {
        runValidationChecks();
    }, [adjustmentData]);
    function getInitialValue() {
        const ledger = 'receivables';
        const shipper_cost = shipment[ledger].filter((adjustment) => selectedLineItem && selectedLineItem._id === adjustment._id)[0];
        return {
            ledger,
            timestamp: (shipper_cost === null || shipper_cost === void 0 ? void 0 : shipper_cost.timestamp) || moment(),
            shipperCost: (shipper_cost === null || shipper_cost === void 0 ? void 0 : shipper_cost.value) || 0,
            reasonCode: null,
            note: '',
            derivedEquipment: (shipper_cost === null || shipper_cost === void 0 ? void 0 : shipper_cost.equipment) || '',
            message: (shipper_cost === null || shipper_cost === void 0 ? void 0 : shipper_cost.message) || '',
            _id: (shipper_cost === null || shipper_cost === void 0 ? void 0 : shipper_cost._id) || null,
        };
    }
    const REASON_CODES = [
        { value: null, localizationSlug: 'REASON_CODE' },
        { value: 'rate_error', localizationSlug: 'RATE_ERROR' },
        { value: 'fuel_error', localizationSlug: 'FUEL_ERROR' },
        { value: 'btf_error', localizationSlug: 'BTF_ERROR' },
        { value: 'accessorial_error', localizationSlug: 'ACCESSORIAL_ERROR' },
        {
            value: 'customer_accommodation',
            localizationSlug: 'CUSTOMER_ACCOMODATION',
        },
        {
            value: 'missing_documentation',
            localizationSlug: 'MISSING_DOCUMENTATION',
        },
        { value: 'missing_approvals', localizationSlug: 'MISSING_APPROVALS' },
        { value: 'duplicate_invoice', localizationSlug: 'DUPLICATE_INVOICE' },
    ];
    function handleError(type, error) {
        setErrors((prev) => {
            let previous = Object.assign({}, prev);
            previous[type] = error;
            return previous;
        });
    }
    function runValidationChecks() {
        var _a;
        if (!adjustmentData.shipperCost) {
            handleError('shipperCost', t('EMPTY_SHIPPER_COST_ERROR'));
        }
        else if (Number(adjustmentData.shipperCost) <= 0) {
            handleError('shipperCost', t('NEGATIVE_SHIPPER_COST_ERROR'));
        }
        else {
            handleError('shipperCost', null);
        }
        if (!((_a = adjustmentData.note) === null || _a === void 0 ? void 0 : _a.length)) {
            handleError('note', t('EMPTY_NOTES_ERROR'));
        }
        else {
            handleError('note', null);
        }
        if (!adjustmentData.reasonCode) {
            handleError('reasonCode', t('REQUIRED_REASON_CODE'));
        }
        else {
            handleError('reasonCode', null);
        }
    }
    function handleSave() {
        let errorsValidated = true;
        const errorsToCheck = Object.keys(errors);
        for (let i = 0; i < errorsToCheck.length; i++) {
            const item = errorsToCheck[i];
            if (errors[item]) {
                errorsValidated = false;
                break;
            }
        }
        if (errorsValidated)
            onSave(adjustmentData);
    }
    const onChange = (type, value) => {
        setAdjustmentData((previous) => {
            let prev = Object.assign({}, previous);
            prev[type] = value;
            return prev;
        });
    };
    const getError = (type) => errors[type];
    const getEquipmentLabel = (equipment) => `${equipment.equipment_id}`;
    const renderModalBody = () => {
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "flex justify-between" },
                React.createElement("div", { className: "w-52" },
                    React.createElement(CargomaticInput, { value: adjustmentData.shipperCost, error: errors.shipperCost, label: t('ADD_SHIPPER_COST'), info: t('ADD_SHIPPER_COST'), placeholder: t('NEW_DOLLAR_VALUE_PLACEHOLDER'), className: "border-l-0", type: "number", onChange: (value) => onChange('shipperCost', value) }),
                    React.createElement(ErrorLabel, { className: "mt-1" }, getError('shipperCost') || '')),
                React.createElement("div", { className: "w-2/3" },
                    React.createElement("fieldset", null,
                        React.createElement("label", { className: "block mb-1 form-label" }, t('EQUIPMENT')),
                        React.createElement("select", { disabled: true, className: "form-select", value: adjustmentData.derivedEquipment }, equipments.map((equipment) => (React.createElement("option", { key: equipment._id, value: equipment._id }, getEquipmentLabel(equipment)))))))),
            React.createElement("div", { className: "flex justify-between " },
                React.createElement("div", { className: "mt-2 flex flex-col justify-between h-20" },
                    React.createElement(CargomaticSelect, { label: t('REASON_CODE'), className: "rounded-md box-content h-5 w-52", onSelect: ({ value }) => onChange('reasonCode', value), value: adjustmentData.reasonCode, placeholder: t('REASON_CODE'), items: REASON_CODES, valueKey: "value", textKey: "localizationSlug" }),
                    React.createElement(ErrorLabel, { className: "mt-1" }, getError('reasonCode') || '')),
                React.createElement("div", { className: "w-2/3 mt-1 flex flex-col justify-between" },
                    React.createElement(CargomaticInput, { maxLength: 100, type: "text", label: t('ENTER_NOTE_LABEL'), placeholder: t('ENTER_NOTE_PLACEHOLDER'), value: adjustmentData.note, onChange: (value) => onChange('note', value) }),
                    React.createElement(ErrorLabel, { className: "mt-1" }, getError('note') || '')))));
    };
    const renderFooter = () => {
        return (React.createElement("div", { className: "flex items-center flex-wrap" },
            React.createElement("div", { className: "form-actions" },
                React.createElement("button", { className: "mr-2 block button button-green", onClick: handleSave },
                    React.createElement(Translation, null, (t) => titleize(t('SAVE')))),
                React.createElement("button", { className: "button button-secondary", onClick: () => onHide() },
                    React.createElement(Translation, null, (t) => t('CANCEL'))))));
    };
    return (React.createElement(CargomaticModal, { dialogClassName: " md:w-1/2", title: t('SHIPPER_COST_ADJUSTMENT'), style: { top: '20%' }, show: showModal, onHide: () => onHide(), backdrop: "static", body: renderModalBody(), footer: renderFooter(), location: "middle" }));
}
